import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Lock,
  Unlock,
  FileCheck,
  CheckCircle2,
  Fingerprint,
  Scan,
  Key,
  Database,
  Cloud,
  Cpu,
  Zap,
  Send,
  Download,
  Upload,
  RefreshCw,
  AlertTriangle,
  XCircle
} from 'lucide-react';

// =============================================================================
// CREDENTIAL ANIMATION TYPES
// =============================================================================

export const AnimationType = {
  ENCRYPT: 'encrypt',
  DECRYPT: 'decrypt',
  VERIFY: 'verify',
  UPLOAD: 'upload',
  TRANSFER: 'transfer',
  LOCK: 'lock',
  UNLOCK: 'unlock',
  SCAN: 'scan',
  APPROVE: 'approve',
  REJECT: 'reject'
};

// =============================================================================
// PARTICLE SYSTEM
// =============================================================================

const Particle = ({ delay, duration, startX, startY, endX, endY, size, color }) => (
  <motion.div
    initial={{ x: startX, y: startY, opacity: 0, scale: 0 }}
    animate={{
      x: [startX, endX],
      y: [startY, endY],
      opacity: [0, 1, 1, 0],
      scale: [0, 1, 1, 0]
    }}
    transition={{
      duration: duration,
      delay: delay,
      ease: "easeOut"
    }}
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      background: color
    }}
  />
);

const DataStream = ({ active, direction = 'up' }) => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    delay: i * 0.1,
    duration: 1.5,
    size: Math.random() * 4 + 2,
    xOffset: Math.random() * 40 - 20
  }));
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {active && particles.map(p => (
        <motion.div
          key={p.id}
          initial={{ 
            y: direction === 'up' ? '100%' : '-20%',
            x: `calc(50% + ${p.xOffset}px)`,
            opacity: 0
          }}
          animate={{ 
            y: direction === 'up' ? '-20%' : '100%',
            opacity: [0, 1, 1, 0]
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute w-1 rounded-full"
          style={{
            height: p.size * 4,
            background: 'linear-gradient(to top, transparent, #06b6d4, transparent)'
          }}
        />
      ))}
    </div>
  );
};

// =============================================================================
// ENCRYPTION ANIMATION
// =============================================================================

export const EncryptionAnimation = ({ 
  isActive, 
  onComplete,
  title = "Encrypting Credential",
  subtitle = "Securing with AES-256 encryption"
}) => {
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    if (!isActive) {
      setPhase(0);
      setProgress(0);
      return;
    }
    
    const phases = [
      { duration: 800, progress: 25 },
      { duration: 600, progress: 50 },
      { duration: 700, progress: 75 },
      { duration: 500, progress: 100 }
    ];
    
    let timeout;
    let currentPhase = 0;
    
    const runPhase = () => {
      if (currentPhase < phases.length) {
        setPhase(currentPhase);
        setProgress(phases[currentPhase].progress);
        timeout = setTimeout(() => {
          currentPhase++;
          runPhase();
        }, phases[currentPhase].duration);
      } else {
        setTimeout(() => onComplete?.(), 300);
      }
    };
    
    runPhase();
    return () => clearTimeout(timeout);
  }, [isActive, onComplete]);
  
  const phaseLabels = [
    "Initializing encryption...",
    "Generating secure keys...",
    "Applying AES-256...",
    "Finalizing secure storage..."
  ];
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative w-96 p-8"
          >
            {/* Central Icon */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              {/* Rotating rings */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 border-2 border-neon-cyan/30 rounded-full"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                className="absolute inset-2 border-2 border-neon-purple/30 rounded-full border-dashed"
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute inset-4 border-2 border-neon-cyan/50 rounded-full"
                style={{ borderStyle: 'dotted' }}
              />
              
              {/* Center lock icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    boxShadow: [
                      '0 0 20px rgba(6, 182, 212, 0.3)',
                      '0 0 40px rgba(6, 182, 212, 0.5)',
                      '0 0 20px rgba(6, 182, 212, 0.3)'
                    ]
                  }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="w-16 h-16 rounded-xl bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center"
                >
                  <Lock className="w-8 h-8 text-white" />
                </motion.div>
              </div>
              
              {/* Data particles */}
              <DataStream active={phase >= 1} direction="up" />
            </div>
            
            {/* Title */}
            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xl font-bold text-white text-center mb-2"
            >
              {title}
            </motion.h3>
            
            <motion.p
              key={phase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-neon-cyan text-center text-sm mb-6"
            >
              {phaseLabels[phase]}
            </motion.p>
            
            {/* Progress bar */}
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-gradient-to-r from-neon-cyan to-neon-purple"
              />
            </div>
            
            <p className="text-slate-500 text-center text-xs">
              {subtitle}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// VERIFICATION ANIMATION
// =============================================================================

export const VerificationAnimation = ({ 
  isActive, 
  onComplete,
  success = true,
  title = "Verifying Credential"
}) => {
  const [phase, setPhase] = useState(0);
  const [scanLine, setScanLine] = useState(0);
  
  useEffect(() => {
    if (!isActive) {
      setPhase(0);
      setScanLine(0);
      return;
    }
    
    // Scan animation
    const scanInterval = setInterval(() => {
      setScanLine(prev => (prev + 2) % 100);
    }, 30);
    
    // Phase progression
    const phaseTimers = [
      setTimeout(() => setPhase(1), 500),
      setTimeout(() => setPhase(2), 1500),
      setTimeout(() => setPhase(3), 2500),
      setTimeout(() => {
        setPhase(4);
        onComplete?.();
      }, 3000)
    ];
    
    return () => {
      clearInterval(scanInterval);
      phaseTimers.forEach(clearTimeout);
    };
  }, [isActive, onComplete]);
  
  const phaseLabels = [
    "Initializing verification...",
    "Scanning document...",
    "Validating authenticity...",
    "Cross-referencing database...",
    success ? "Verification Complete!" : "Verification Failed"
  ];
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative w-96 p-8"
          >
            {/* Document with scan effect */}
            <div className="relative w-48 h-64 mx-auto mb-8 bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
              {/* Document lines */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="h-2 mx-4 my-3 rounded bg-slate-700/50"
                  style={{ width: `${60 + Math.random() * 30}%` }}
                />
              ))}
              
              {/* Scan line */}
              <motion.div
                animate={{ top: `${scanLine}%` }}
                className="absolute left-0 right-0 h-1"
                style={{
                  background: 'linear-gradient(90deg, transparent, #06b6d4, transparent)',
                  boxShadow: '0 0 20px #06b6d4'
                }}
              />
              
              {/* Corner brackets */}
              <div className="absolute top-2 left-2 w-6 h-6 border-l-2 border-t-2 border-neon-cyan" />
              <div className="absolute top-2 right-2 w-6 h-6 border-r-2 border-t-2 border-neon-cyan" />
              <div className="absolute bottom-2 left-2 w-6 h-6 border-l-2 border-b-2 border-neon-cyan" />
              <div className="absolute bottom-2 right-2 w-6 h-6 border-r-2 border-b-2 border-neon-cyan" />
              
              {/* Verification result overlay */}
              {phase === 4 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute inset-0 flex items-center justify-center bg-black/60"
                >
                  {success ? (
                    <CheckCircle2 className="w-20 h-20 text-neon-green" />
                  ) : (
                    <XCircle className="w-20 h-20 text-red-500" />
                  )}
                </motion.div>
              )}
            </div>
            
            {/* Verification steps */}
            <div className="flex justify-center space-x-3 mb-6">
              {[Scan, Database, Shield, CheckCircle2].map((Icon, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0.3 }}
                  animate={{ 
                    opacity: phase >= i ? 1 : 0.3,
                    scale: phase === i ? 1.2 : 1
                  }}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    phase >= i ? 'bg-neon-cyan/20' : 'bg-slate-800'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${phase >= i ? 'text-neon-cyan' : 'text-slate-600'}`} />
                </motion.div>
              ))}
            </div>
            
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-white text-center mb-2"
            >
              {title}
            </motion.h3>
            
            <motion.p
              key={phase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-center text-sm ${
                phase === 4 
                  ? success ? 'text-neon-green' : 'text-red-400'
                  : 'text-neon-cyan'
              }`}
            >
              {phaseLabels[phase]}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// FILE TRANSFER ANIMATION
// =============================================================================

export const TransferAnimation = ({ 
  isActive, 
  onComplete,
  fromLabel = "Source",
  toLabel = "Destination",
  title = "Transferring Credential"
}) => {
  const [progress, setProgress] = useState(0);
  const [packets, setPackets] = useState([]);
  
  useEffect(() => {
    if (!isActive) {
      setProgress(0);
      setPackets([]);
      return;
    }
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => onComplete?.(), 500);
          return 100;
        }
        return prev + 2;
      });
    }, 50);
    
    // Packet animation
    const packetInterval = setInterval(() => {
      setPackets(prev => {
        const newPacket = {
          id: Date.now(),
          progress: 0
        };
        return [...prev.slice(-10), newPacket];
      });
    }, 200);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(packetInterval);
    };
  }, [isActive, onComplete]);
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative w-[450px] p-8"
          >
            <h3 className="text-xl font-bold text-white text-center mb-8">{title}</h3>
            
            {/* Transfer visualization */}
            <div className="flex items-center justify-between mb-8">
              {/* Source */}
              <div className="text-center">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="w-20 h-20 rounded-xl bg-gradient-to-br from-slate-800 to-slate-700 border border-slate-600 flex items-center justify-center mb-2"
                >
                  <Database className="w-8 h-8 text-neon-cyan" />
                </motion.div>
                <span className="text-sm text-slate-400">{fromLabel}</span>
              </div>
              
              {/* Transfer line */}
              <div className="flex-1 mx-4 h-2 bg-slate-800 rounded-full relative overflow-hidden">
                {/* Animated packets */}
                {packets.map((packet, i) => (
                  <motion.div
                    key={packet.id}
                    initial={{ left: '0%' }}
                    animate={{ left: '100%' }}
                    transition={{ duration: 1, ease: "linear" }}
                    className="absolute top-0 w-4 h-2 rounded-full bg-neon-cyan"
                    style={{ boxShadow: '0 0 10px #06b6d4' }}
                  />
                ))}
                
                {/* Progress fill */}
                <motion.div
                  animate={{ width: `${progress}%` }}
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-neon-cyan/50 to-neon-cyan"
                />
              </div>
              
              {/* Destination */}
              <div className="text-center">
                <motion.div
                  animate={{ 
                    scale: progress > 80 ? [1, 1.1, 1] : 1,
                    borderColor: progress > 80 ? '#06b6d4' : '#475569'
                  }}
                  transition={{ duration: 0.5, repeat: progress > 80 ? Infinity : 0 }}
                  className="w-20 h-20 rounded-xl bg-gradient-to-br from-slate-800 to-slate-700 border-2 flex items-center justify-center mb-2"
                >
                  <Cloud className="w-8 h-8 text-neon-purple" />
                </motion.div>
                <span className="text-sm text-slate-400">{toLabel}</span>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-neon-cyan">{progress}%</div>
                <div className="text-xs text-slate-500">Progress</div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-neon-green">256-bit</div>
                <div className="text-xs text-slate-500">Encryption</div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-neon-purple">Secure</div>
                <div className="text-xs text-slate-500">Channel</div>
              </div>
            </div>
            
            <p className="text-center text-sm text-slate-500">
              Secure transfer in progress. Do not close this window.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// VAULT LOCK ANIMATION
// =============================================================================

export const VaultLockAnimation = ({ 
  isActive, 
  onComplete,
  isLocking = true,
  title
}) => {
  const [phase, setPhase] = useState(0);
  const [rotation, setRotation] = useState(0);
  
  useEffect(() => {
    if (!isActive) {
      setPhase(0);
      setRotation(0);
      return;
    }
    
    // Dial rotation
    const rotationInterval = setInterval(() => {
      setRotation(prev => prev + 15);
    }, 100);
    
    // Phase progression
    const timer1 = setTimeout(() => setPhase(1), 500);
    const timer2 = setTimeout(() => setPhase(2), 1200);
    const timer3 = setTimeout(() => {
      setPhase(3);
      clearInterval(rotationInterval);
    }, 1800);
    const timer4 = setTimeout(() => onComplete?.(), 2200);
    
    return () => {
      clearInterval(rotationInterval);
      [timer1, timer2, timer3, timer4].forEach(clearTimeout);
    };
  }, [isActive, onComplete]);
  
  const defaultTitle = isLocking ? "Securing Credential" : "Accessing Credential";
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative w-96 p-8"
          >
            {/* Vault door */}
            <div className="relative w-48 h-48 mx-auto mb-8">
              {/* Outer ring */}
              <motion.div
                animate={{ rotate: rotation }}
                className="absolute inset-0 rounded-full border-8 border-slate-700"
                style={{
                  background: 'conic-gradient(from 0deg, #0f172a, #1e293b, #0f172a)'
                }}
              >
                {/* Notches */}
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-4 bg-slate-500"
                    style={{
                      left: '50%',
                      top: '0',
                      transform: `translateX(-50%) rotate(${i * 30}deg)`,
                      transformOrigin: '50% 96px'
                    }}
                  />
                ))}
              </motion.div>
              
              {/* Inner circle */}
              <div className="absolute inset-8 rounded-full bg-slate-900 border-4 border-slate-600 flex items-center justify-center">
                <motion.div
                  animate={{ 
                    scale: phase >= 3 ? [1, 1.2, 1] : 1,
                    rotate: phase >= 3 ? (isLocking ? 90 : -90) : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {isLocking ? (
                    <Lock className={`w-12 h-12 ${phase >= 3 ? 'text-neon-green' : 'text-slate-500'}`} />
                  ) : (
                    <Unlock className={`w-12 h-12 ${phase >= 3 ? 'text-neon-cyan' : 'text-slate-500'}`} />
                  )}
                </motion.div>
              </div>
              
              {/* Bolts */}
              {[0, 90, 180, 270].map((angle, i) => (
                <motion.div
                  key={angle}
                  animate={{ 
                    x: phase >= 2 ? (isLocking ? 0 : Math.cos(angle * Math.PI / 180) * 15) : (isLocking ? Math.cos(angle * Math.PI / 180) * 15 : 0),
                    y: phase >= 2 ? (isLocking ? 0 : Math.sin(angle * Math.PI / 180) * 15) : (isLocking ? Math.sin(angle * Math.PI / 180) * 15 : 0)
                  }}
                  className="absolute w-4 h-4 bg-slate-600 rounded"
                  style={{
                    left: `calc(50% + ${Math.cos(angle * Math.PI / 180) * 60}px - 8px)`,
                    top: `calc(50% + ${Math.sin(angle * Math.PI / 180) * 60}px - 8px)`
                  }}
                />
              ))}
            </div>
            
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-white text-center mb-2"
            >
              {title || defaultTitle}
            </motion.h3>
            
            <motion.p
              key={phase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-neon-cyan text-center text-sm"
            >
              {phase === 0 && "Authenticating..."}
              {phase === 1 && "Rotating tumblers..."}
              {phase === 2 && (isLocking ? "Engaging locks..." : "Disengaging locks...")}
              {phase === 3 && (isLocking ? "Credential secured!" : "Access granted!")}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// FINGERPRINT SCAN ANIMATION
// =============================================================================

export const FingerprintAnimation = ({ 
  isActive, 
  onComplete,
  success = true,
  title = "Biometric Verification"
}) => {
  const [scanProgress, setScanProgress] = useState(0);
  const [phase, setPhase] = useState(0);
  
  useEffect(() => {
    if (!isActive) {
      setScanProgress(0);
      setPhase(0);
      return;
    }
    
    const scanInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(scanInterval);
          return 100;
        }
        return prev + 4;
      });
    }, 50);
    
    const timer1 = setTimeout(() => setPhase(1), 1000);
    const timer2 = setTimeout(() => setPhase(2), 2000);
    const timer3 = setTimeout(() => {
      setPhase(3);
      onComplete?.();
    }, 2500);
    
    return () => {
      clearInterval(scanInterval);
      [timer1, timer2, timer3].forEach(clearTimeout);
    };
  }, [isActive, onComplete]);
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative w-80 p-8"
          >
            {/* Fingerprint container */}
            <div className="relative w-40 h-48 mx-auto mb-6">
              {/* Scanner background */}
              <div className="absolute inset-0 rounded-2xl bg-slate-900 border-2 border-slate-700 overflow-hidden">
                {/* Fingerprint icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Fingerprint 
                    className={`w-24 h-24 transition-colors duration-500 ${
                      phase >= 3 
                        ? success ? 'text-neon-green' : 'text-red-500'
                        : 'text-slate-600'
                    }`} 
                  />
                </div>
                
                {/* Scan line */}
                <motion.div
                  animate={{ top: `${scanProgress}%` }}
                  className="absolute left-0 right-0 h-0.5 bg-neon-cyan"
                  style={{ boxShadow: '0 0 20px #06b6d4, 0 0 40px #06b6d4' }}
                />
                
                {/* Corner accents */}
                <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-neon-cyan" />
                <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-neon-cyan" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-neon-cyan" />
                <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-neon-cyan" />
              </div>
              
              {/* Pulse rings */}
              {phase >= 2 && (
                <>
                  <motion.div
                    initial={{ scale: 0.8, opacity: 1 }}
                    animate={{ scale: 1.5, opacity: 0 }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className={`absolute inset-0 rounded-2xl border-2 ${
                      success ? 'border-neon-green' : 'border-red-500'
                    }`}
                  />
                  <motion.div
                    initial={{ scale: 0.8, opacity: 1 }}
                    animate={{ scale: 1.5, opacity: 0 }}
                    transition={{ duration: 1, repeat: Infinity, delay: 0.3 }}
                    className={`absolute inset-0 rounded-2xl border-2 ${
                      success ? 'border-neon-green' : 'border-red-500'
                    }`}
                  />
                </>
              )}
            </div>
            
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-white text-center mb-2"
            >
              {title}
            </motion.h3>
            
            <motion.p
              key={phase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-center text-sm ${
                phase >= 3 
                  ? success ? 'text-neon-green' : 'text-red-400'
                  : 'text-neon-cyan'
              }`}
            >
              {phase === 0 && "Place finger on scanner..."}
              {phase === 1 && "Scanning biometric data..."}
              {phase === 2 && "Analyzing patterns..."}
              {phase === 3 && (success ? "Identity Verified!" : "Verification Failed")}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// APPROVAL/REJECTION ANIMATION
// =============================================================================

export const ApprovalAnimation = ({ 
  isActive, 
  onComplete,
  isApproved = true,
  title
}) => {
  const [phase, setPhase] = useState(0);
  
  useEffect(() => {
    if (!isActive) {
      setPhase(0);
      return;
    }
    
    const timer1 = setTimeout(() => setPhase(1), 300);
    const timer2 = setTimeout(() => setPhase(2), 800);
    const timer3 = setTimeout(() => {
      setPhase(3);
      onComplete?.();
    }, 1500);
    
    return () => [timer1, timer2, timer3].forEach(clearTimeout);
  }, [isActive, onComplete]);
  
  const Icon = isApproved ? CheckCircle2 : XCircle;
  const color = isApproved ? 'neon-green' : 'red-500';
  const defaultTitle = isApproved ? "Credential Approved" : "Credential Rejected";
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            className="relative"
          >
            {/* Burst effect */}
            {phase >= 2 && (
              <>
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ 
                      scale: 3,
                      opacity: 0,
                      x: Math.cos(i * 30 * Math.PI / 180) * 100,
                      y: Math.sin(i * 30 * Math.PI / 180) * 100
                    }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className={`absolute w-4 h-4 rounded-full bg-${color}`}
                    style={{ left: '50%', top: '50%', marginLeft: -8, marginTop: -8 }}
                  />
                ))}
              </>
            )}
            
            {/* Main icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ 
                scale: phase >= 1 ? 1 : 0,
                rotate: phase >= 1 ? 0 : -180
              }}
              transition={{ type: "spring", damping: 10 }}
              className="relative"
            >
              <motion.div
                animate={{ 
                  boxShadow: phase >= 2 ? [
                    `0 0 20px ${isApproved ? '#22c55e' : '#ef4444'}`,
                    `0 0 60px ${isApproved ? '#22c55e' : '#ef4444'}`,
                    `0 0 20px ${isApproved ? '#22c55e' : '#ef4444'}`
                  ] : 'none'
                }}
                transition={{ duration: 1, repeat: phase >= 2 ? Infinity : 0 }}
                className={`w-32 h-32 rounded-full bg-${color}/20 flex items-center justify-center`}
              >
                <Icon className={`w-20 h-20 text-${color}`} />
              </motion.div>
            </motion.div>
            
            {/* Title */}
            {phase >= 2 && (
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`text-2xl font-bold text-${color} text-center mt-6`}
              >
                {title || defaultTitle}
              </motion.h3>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// HOOK FOR EASY USE
// =============================================================================

export const useCredentialAnimation = () => {
  const [activeAnimation, setActiveAnimation] = useState(null);
  const [animationProps, setAnimationProps] = useState({});
  
  const showAnimation = (type, props = {}) => {
    return new Promise((resolve) => {
      setAnimationProps({
        ...props,
        onComplete: () => {
          setActiveAnimation(null);
          resolve();
        }
      });
      setActiveAnimation(type);
    });
  };
  
  const encrypt = (props) => showAnimation(AnimationType.ENCRYPT, props);
  const verify = (props) => showAnimation(AnimationType.VERIFY, props);
  const transfer = (props) => showAnimation(AnimationType.TRANSFER, props);
  const lock = (props) => showAnimation(AnimationType.LOCK, { ...props, isLocking: true });
  const unlock = (props) => showAnimation(AnimationType.UNLOCK, { ...props, isLocking: false });
  const scan = (props) => showAnimation(AnimationType.SCAN, props);
  const approve = (props) => showAnimation(AnimationType.APPROVE, { ...props, isApproved: true });
  const reject = (props) => showAnimation(AnimationType.REJECT, { ...props, isApproved: false });
  
  const AnimationComponent = () => (
    <>
      <EncryptionAnimation 
        isActive={activeAnimation === AnimationType.ENCRYPT} 
        {...animationProps}
      />
      <VerificationAnimation 
        isActive={activeAnimation === AnimationType.VERIFY} 
        {...animationProps}
      />
      <TransferAnimation 
        isActive={activeAnimation === AnimationType.TRANSFER} 
        {...animationProps}
      />
      <VaultLockAnimation 
        isActive={activeAnimation === AnimationType.LOCK || activeAnimation === AnimationType.UNLOCK} 
        {...animationProps}
      />
      <FingerprintAnimation 
        isActive={activeAnimation === AnimationType.SCAN} 
        {...animationProps}
      />
      <ApprovalAnimation 
        isActive={activeAnimation === AnimationType.APPROVE || activeAnimation === AnimationType.REJECT} 
        {...animationProps}
      />
    </>
  );
  
  return {
    encrypt,
    verify,
    transfer,
    lock,
    unlock,
    scan,
    approve,
    reject,
    AnimationComponent,
    isAnimating: activeAnimation !== null
  };
};

export default {
  EncryptionAnimation,
  VerificationAnimation,
  TransferAnimation,
  VaultLockAnimation,
  FingerprintAnimation,
  ApprovalAnimation,
  useCredentialAnimation
};
